import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { styles, colors, useIsMobile } from './UI';

// Compact 7-day calendar for the Dashboard. Same data sources as the full
// /calendar page (project starts, follow-ups, tasks) but rendered tighter.
//
// scope: 'mine' (default) shows the caller's items only. 'team' uses the
// server's accessible-reps logic so a supervisor sees their branch reps.
// kind: filters follow-ups + tasks to Base or Project. Projects are
// always project-kind; the filter doesn't apply to them.
export default function CalendarWidget({ scope = 'mine', kind = '' } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  // Anchor on today, 7 days forward. Re-anchor only when the day rolls
  // (which we don't bother detecting -- a page refresh is enough).
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const start = isoDate(today);
  const end = isoDate(addDays(today, 6));

  useEffect(() => {
    const params = { start, end };
    if (scope) params.scope = scope;
    if (kind) params.kind = kind;
    api.calendar(params).then(setData).catch(e => setError(e.message));
  }, [start, end, scope, kind]);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) arr.push(addDays(today, i));
    return arr;
  }, [today]);

  const byDay = useMemo(() => {
    const m = {};
    days.forEach(d => { m[isoDate(d)] = { projects: [], follow_ups: [], tasks: [], events: [], holidays: [] }; });
    if (!data) return m;
    for (const p of data.projects) {
      const k = String(p.date_planned).slice(0, 10);
      if (m[k]) m[k].projects.push(p);
    }
    for (const f of data.follow_ups) {
      const k = String(f.follow_up_date).slice(0, 10);
      if (m[k]) m[k].follow_ups.push(f);
    }
    for (const t of data.tasks) {
      const k = String(t.due_date).slice(0, 10);
      if (m[k]) m[k].tasks.push(t);
    }
    for (const e of (data.events || [])) {
      const k = String(e.event_date).slice(0, 10);
      if (m[k]) m[k].events.push(e);
    }
    for (const h of (data.holidays || [])) {
      if (m[h.date]) m[h.date].holidays.push(h);
    }
    return m;
  }, [data, days]);

  // Emoji per event type -- matches Calendar.jsx + the server invite email.
  const eventEmoji = {
    'Luncheon': '🍽️', 'Dinner': '🍷', 'Meeting': '🤝', 'Lunch & Learn': '🎓',
    'Golf': '⛳', 'Presentation': '🎤', 'Trade Show': '🏪',
    'Network Meeting': '🌐', 'Job Walk': '👷', 'Other': '📅'
  };

  const todayStr = isoDate(today);
  const isMobile = useIsMobile();

  // One day's chips — shared by the desktop grid cells and the mobile agenda
  // rows. wrap=true (mobile) lets chip text wrap instead of ellipsizing,
  // since the agenda row has the full screen width.
  const chipsFor = (cell, wrap) => {
    const cs = (bg, fg) => wrap
      ? { ...chipStyle(bg, fg), whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', fontSize: 12, padding: '3px 8px' }
      : chipStyle(bg, fg);
    return (
      <>
        {cell.holidays.map((h, i) => (
          <div key={'h' + i} title={h.federal ? 'US Federal Holiday' : (h.state ? `${h.state} state holiday` : 'Observance')}
               style={cs('#FEE2E2', '#991B1B')}>
            🎉 {h.name}
          </div>
        ))}
        {cell.tasks.map(t => (
          <div key={'t' + t.id} title={t.user_name ? `${t.user_name}: ${t.title}` : t.title}
               style={cs('#DBEAFE', '#1E40AF')}>
            ✓ {t.title}
          </div>
        ))}
        {cell.follow_ups.map(f => (
          <Link key={'f' + f.id} to={`/accounts/${encodeURIComponent(f.customer)}`}
                style={{ textDecoration: 'none' }}
                title={f.rep_name ? `${f.rep_name} → ${f.customer}` : `Follow up with ${f.customer}`}>
            <div style={cs('#FEE2E2', '#991B1B')}>
              📞 {f.customer}
            </div>
          </Link>
        ))}
        {cell.projects.map(p => (
          <Link key={'p' + p.id} to={`/accounts/${encodeURIComponent(p.site)}`}
                style={{ textDecoration: 'none' }}
                title={`${p.site} (${p.status})${p.account_owner_name ? ' — ' + p.account_owner_name : ''}`}>
            <div style={cs(statusBg(p.status), statusFg(p.status))}>
              🏗 {p.site}
            </div>
          </Link>
        ))}
        {cell.events.map(e => (
          <Link key={'e' + e.id} to="/calendar"
                style={{ textDecoration: 'none' }}
                title={`${e.title}${e.customer ? ' · ' + e.customer : ''}${e.location ? ' · ' + e.location : ''}`}>
            <div style={cs('#EDE9FE', '#5B21B6')}>
              {eventEmoji[e.event_type] || '📅'} {e.title}
            </div>
          </Link>
        ))}
      </>
    );
  };

  return (
    <div style={styles.card}>
      <h2 style={{ ...styles.h2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span>
          📅 Next 7 Days
          <span style={{ color: colors.textMuted, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            {scope === 'team' ? '· Team' : '· Mine'}
            {kind && ` · ${kind}`}
          </span>
        </span>
        <Link to="/calendar" style={{ fontSize: 12, color: colors.orange, textDecoration: 'none', fontWeight: 600 }}>
          Open full calendar →
        </Link>
      </h2>

      {error && <div style={styles.errorBox}>{error}</div>}

      {isMobile ? (
        /* Phone: 7 columns at ~46px each is unreadable, so render an agenda —
           one row per day, day label on the left, full-width chips on the
           right. Same data, same chips (wrapping instead of ellipsized). */
        <div>
          {days.map(d => {
            const k = isoDate(d);
            const cell = byDay[k] || { projects: [], follow_ups: [], tasks: [], events: [], holidays: [] };
            const isToday = k === todayStr;
            const total = cell.projects.length + cell.follow_ups.length + cell.tasks.length + cell.events.length + cell.holidays.length;
            return (
              <div key={k} style={{
                display: 'flex', gap: 12, padding: '8px 6px',
                borderBottom: `1px solid ${colors.border}`,
                background: isToday ? '#FFFBEB' : 'transparent', borderRadius: isToday ? 6 : 0
              }}>
                <div style={{ flex: '0 0 46px', textAlign: 'center', paddingTop: 2 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: isToday ? '#92400E' : colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {d.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: isToday ? '#92400E' : colors.text }}>{d.getDate()}</div>
                  {isToday && <div style={{ fontSize: 9, fontWeight: 700, color: '#92400E' }}>today</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
                  {total === 0
                    ? <div style={{ fontSize: 12, color: '#D1D5DB', paddingTop: 6 }}>—</div>
                    : chipsFor(cell, true)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
        {days.map(d => {
          const k = isoDate(d);
          const cell = byDay[k] || { projects: [], follow_ups: [], tasks: [], events: [], holidays: [] };
          const isToday = k === todayStr;
          const isHoliday = cell.holidays.length > 0;
          const total = cell.projects.length + cell.follow_ups.length + cell.tasks.length + cell.events.length;
          // Holiday cells get a soft pink tint so they pop visually.
          const cellBg = isToday ? '#FEF3C7' : (isHoliday ? '#FEF2F2' : '#fff');
          const cellBorder = isToday ? '#F59E0B' : (isHoliday ? '#FCA5A5' : colors.border);
          return (
            <div key={k} style={{
              background: cellBg,
              border: `1px solid ${cellBorder}`,
              borderRadius: 6, padding: 6, minHeight: 110, fontSize: 11,
              // minWidth 0 + overflow hidden: without them each grid column's
              // minimum is its longest nowrap chip (customer/site names), and
              // 7 columns of that forced the whole document ~4x wider than a
              // phone screen — iOS zoomed the Dashboard out to a sliver. The
              // chips already ellipsize; let the 1fr tracks shrink.
              minWidth: 0, overflow: 'hidden'
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: isToday ? '#92400E' : colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                {d.toLocaleDateString('en-US', { weekday: 'short' })}
                <span style={{ marginLeft: 4, color: isToday ? '#92400E' : colors.text }}>{d.getDate()}</span>
                {isToday && <span style={{ marginLeft: 4, fontSize: 9 }}>· today</span>}
              </div>

              {chipsFor(cell, false)}

              {total === 0 && (
                <div style={{ fontSize: 10, color: '#D1D5DB', textAlign: 'center', marginTop: 16 }}>—</div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

const chipStyle = (bg, fg) => ({
  display: 'block', background: bg, color: fg, fontSize: 10, fontWeight: 600,
  padding: '2px 4px', borderRadius: 3, marginBottom: 2,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
});

const statusBg = (s) => ({
  Bidding: '#FEF3C7', Won: '#D1FAE5', Active: '#DBEAFE',
  Complete: '#F3F4F6', Lost: '#FEE2E2'
})[s] || '#F3F4F6';

const statusFg = (s) => ({
  Bidding: '#92400E', Won: '#065F46', Active: '#1E40AF',
  Complete: '#6B7280', Lost: '#991B1B'
})[s] || '#1F2937';

function addDays(d, n) {
  const x = new Date(d); x.setDate(x.getDate() + n); return x;
}
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
